import { useState, useEffect, useCallback } from "react";
import { supabase, BRAND, DANGER, MUTED, text, btn, chip, linkBtn, errorBanner, SHADOW_MODAL, SHADOW_POP, LINE, WHITE, INK, TEXT, FAINT, ROW_LINE, SURFACE, GREEN_TEXT, SCRIM } from "./constants";
import { CloseButton, ConfirmBar, MoreMenu } from "./shared";
import SalaryReco from "./SalaryReco";
import { loadPersonData, findExistingCandidacy, createCandidateFromPerson } from "./lib/personCandidate";
import { aiFetch } from "./lib/ai";
import { useIsMobile, MOBILE_BOTTOM } from "./lib/responsive";
import { X, Check, Plus, MessageSquare, TriangleAlert, ChevronDown } from "lucide-react";

// ─── MAPPING ──────────────────────────────────────────────────────────────────
// La long list previa al contacto: el founder y su equipo validan el mercado
// antes de que Rutment contacte a nadie. Dos bloques:
//   1. Empresas objetivo (no son datos personales): el cliente valida, veta con
//      motivo, comenta y propone empresas.
//   2. Perfiles ANONIMIZADOS: el cliente valida, veta y comenta; no los crea.
//
// PROTECCIÓN DE DATOS (no negociable): el cliente nunca recibe nombre, foto,
// contacto, salario, edad, notas ni la EMPRESA ACTUAL de una persona del
// mapping. Se garantiza en la base: mapping_profiles no tiene ninguna columna
// que apunte a la persona; el cruce vive en mapping_profile_links, solo admin
// (RLS is_admin). Tampoco se enseña al cliente a qué empresa objetivo
// corresponde un perfil: "CFO de <empresa>" identifica a la persona. Los
// nombres solo aparecen cuando alguien pasa a Candidatos (con consentimiento).
// El cliente solo ve perfiles publicados, y solo puede cambiar estado y motivo
// del veto (trigger mapping_client_update_guard). Los avisos al admin los crea
// la base (trigger mapping_notify_admin).
//
// PANTALLA: una lista de comprobación. Cada empresa o perfil es una fila con
// ✓ (validar) y ✕ (vetar); pulsar la fila la despliega debajo (detalle, motivo
// del veto y comentarios). Primero lo pendiente, luego lo validado; lo vetado
// queda detrás de "Ver vetadas".

const EMPTY_PROFILE = { role_title: "", company_descriptor: "", years_experience: "", location: "", background: "", highlights: "", contact: null, company_id: "" };

const MappingTab = ({ position, lang, isAdmin, companyName = "", setOpenPositionsList = () => {}, setPhotoUrls = () => {} }) => {
  const es = lang === "es";
  const T = (a, b) => (es ? a : b);
  const [companies, setCompanies] = useState(null);
  const [profiles, setProfiles] = useState(null);
  const [comments, setComments] = useState([]);
  const [links, setLinks] = useState({});
  const [approval, setApproval] = useState({ at: null, by: null });
  const [loadErr, setLoadErr] = useState("");
  const [msg, setMsg] = useState(null);
  const [me, setMe] = useState("");
  const [open, setOpen] = useState(null);           // fila desplegada: { kind, id } (una cada vez)
  const [vetoing, setVetoing] = useState(false);    // pidiendo el motivo del veto en la fila desplegada
  const [vetoText, setVetoText] = useState("");
  const [commentText, setCommentText] = useState("");
  const [ask, setAsk] = useState(null);             // confirmación en la página: { type: "delete"|"promote", kind, id }
  const [showVetoed, setShowVetoed] = useState({}); // { company: true }
  const [openSec, setOpenSec] = useState({});       // móvil: secciones desplegadas (contraídas al entrar)
  const [addingCo, setAddingCo] = useState(false);
  const [newCo, setNewCo] = useState({ name: "", sector: "" });
  const [pf, setPf] = useState(null);               // formulario de perfil (admin)
  const [pfEditing, setPfEditing] = useState(null);
  const [pfConfirm, setPfConfirm] = useState(false);
  const [contactQ, setContactQ] = useState("");
  const [contactHits, setContactHits] = useState([]);
  const [drafting, setDrafting] = useState(false);
  const [busy, setBusy] = useState(false);
  const isMobile = useIsMobile();

  const load = useCallback(async () => {
    setLoadErr("");
    const [c, p, m, pos] = await Promise.all([
      supabase.from("mapping_companies").select("*").eq("position_id", position.id).order("created_at"),
      supabase.from("mapping_profiles").select("*").eq("position_id", position.id).order("sort_order", { nullsFirst: false }).order("created_at"),
      supabase.from("mapping_comments").select("*").eq("position_id", position.id).order("created_at"),
      supabase.from("positions").select("mapping_approved_at, mapping_approved_by").eq("id", position.id).maybeSingle(),
    ]);
    if (c.error || p.error) { setLoadErr((c.error || p.error).message); setCompanies([]); setProfiles([]); return; }
    setCompanies(c.data || []); setProfiles(p.data || []); setComments(m.data || []);
    setApproval({ at: pos.data?.mapping_approved_at || null, by: pos.data?.mapping_approved_by || null });
    if (isAdmin && (p.data || []).length) {
      const { data: l } = await supabase.from("mapping_profile_links").select("*").in("profile_id", p.data.map(x => x.id));
      setLinks(Object.fromEntries((l || []).map(x => [x.profile_id, x])));
    }
  }, [position.id, isAdmin]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => setMe(session?.user?.email || ""));
  }, []);

  const flash = (ok, text) => { setMsg({ ok, text }); setTimeout(() => setMsg(null), 4000); };
  const authorLabel = isAdmin ? "Rutment" : (me ? me.split("@")[0] : (companyName || T("Cliente", "Client")));

  // ── Escrituras (siempre con .select(): RLS bloqueado = 0 filas sin error) ──
  const setStatus = async (kind, row, status, veto_reason = null) => {
    const table = kind === "company" ? "mapping_companies" : "mapping_profiles";
    const { data, error } = await supabase.from(table).update({ status, veto_reason: status === "vetoed" ? veto_reason : null }).eq("id", row.id).select();
    if (error || !data?.length) { flash(false, T("No se pudo guardar: ", "Couldn't save: ") + (error?.message || T("sin permiso", "no permission"))); return; }
    (kind === "company" ? setCompanies : setProfiles)(list => list.map(x => (x.id === row.id ? data[0] : x)));
    setVetoing(false); setVetoText("");
    // Lo vetado se esconde detrás de "Ver vetadas": se dice dónde ha ido
    if (status === "vetoed" && !showVetoed[kind]) {
      setOpen(null);
      flash(true, T("Vetado. Lo tienes al final, en «Ver vetadas».", "Vetoed. You'll find it at the end, under “Show vetoed”."));
    }
  };

  const addCompany = async () => {
    if (!newCo.name.trim()) return;
    const row = { position_id: position.id, name: newCo.name.trim(), sector: newCo.sector.trim() || null, reason: null, added_by_role: isAdmin ? "admin" : "client", status: isAdmin ? "validated" : "proposed" };
    const { data, error } = await supabase.from("mapping_companies").insert(row).select();
    if (error || !data?.length) { flash(false, T("No se pudo añadir: ", "Couldn't add: ") + (error?.message || "")); return; }
    setCompanies(list => [...list, data[0]]); setNewCo({ name: "", sector: "" }); setAddingCo(false);
  };

  // Se llama desde la confirmación en la página (no hay window.confirm)
  const removeRow = async (kind, row) => {
    setAsk(null);
    const table = kind === "company" ? "mapping_companies" : "mapping_profiles";
    const { data, error } = await supabase.from(table).delete().eq("id", row.id).select("id");
    if (error || !data?.length) { flash(false, T("No se pudo borrar", "Couldn't delete")); return; }
    await supabase.from("mapping_comments").delete().eq("target_type", kind).eq("target_id", row.id);
    (kind === "company" ? setCompanies : setProfiles)(list => list.filter(x => x.id !== row.id));
    setOpen(null);
  };

  const addComment = async (kind, id) => {
    const body = commentText.trim();
    if (!body) return;
    const { data, error } = await supabase.from("mapping_comments").insert({ position_id: position.id, target_type: kind, target_id: id, author_label: authorLabel, body }).select();
    if (error || !data?.length) { flash(false, T("No se pudo comentar: ", "Couldn't comment: ") + (error?.message || "")); return; }
    setComments(list => [...list, data[0]]); setCommentText("");
  };

  const approve = async (yes) => {
    const { data, error } = await supabase.rpc("approve_mapping", { p_position: position.id, p_approve: yes });
    if (error || !data?.ok) { flash(false, T("No se pudo guardar la aprobación", "Couldn't save the approval")); return; }
    const at = yes ? new Date().toISOString() : null;
    setApproval(yes ? { at, by: me } : { at: null, by: null });
    // La Home lee mappingApprovedAt para su aviso de "mapping por aprobar"
    setOpenPositionsList(prev => prev.map(x => (x.id === position.id ? { ...x, mappingApprovedAt: at } : x)));
  };

  // ── Perfiles (admin) ──────────────────────────────────────────────────────
  const searchContacts = async (q) => {
    setContactQ(q);
    if (q.trim().length < 2) { setContactHits([]); return; }
    const { data } = await supabase.from("contacts").select("id, person_id, name, position, company, sector, city, linkedin, email").ilike("name", `%${q.trim()}%`).limit(8);
    setContactHits(data || []);
  };

  // La IA propone el descriptor anónimo; el admin lo revisa antes de publicar
  const pickContact = async (ct) => {
    setContactHits([]); setContactQ(ct.name);
    setPf(f => ({ ...f, contact: ct }));
    setDrafting(true);
    try {
      const res = await aiFetch({ body: JSON.stringify({
        max_tokens: 600,
        system: `You anonymise a professional for a recruiting long list shown to a client. NEVER include the person's name, their current or past employer names, or anything that identifies them uniquely. Describe the company GENERICALLY (sector, stage, size: e.g. "SaaS scale-up, Series B", "Big4", "industrial mid-market"). Return ONLY JSON: {"role_title":"generic title","company_descriptor":"generic","years_experience":number or null,"location":"city or region","background":"one short line: studies + type of career, no company names","highlights":["2-3 short points, no names"]}. ${es ? "Write in Spanish." : "Write in English."}`,
        messages: [{ role: "user", content: `Role: ${ct.position || ""}\nCurrent company (DO NOT NAME IT): ${ct.company || ""}\nSector: ${ct.sector || ""}\nCity: ${ct.city || ""}` }],
      }) });
      const data = await res.json();
      const j = JSON.parse((data.content?.[0]?.text || "{}").replace(/```json|```/g, "").trim());
      setPf(f => ({ ...f, role_title: j.role_title || f.role_title, company_descriptor: j.company_descriptor || "", years_experience: j.years_experience ?? "", location: j.location || ct.city || "", background: j.background || "", highlights: (j.highlights || []).join("\n") }));
    } catch (e) {
      setPf(f => ({ ...f, role_title: f.role_title || ct.position || "", location: f.location || ct.city || "" }));
    }
    setDrafting(false);
  };

  // Avisos antes de publicar: nombre real de la empresa en el texto, persona
  // que trabaja en el cliente o en una empresa vetada
  const warningsFor = (f) => {
    if (!f) return [];
    const w = [];
    const text = [f.role_title, f.company_descriptor, f.background, f.highlights].join(" ").toLowerCase();
    const curCo = (f.contact?.company || "").trim().toLowerCase();
    if (curCo.length >= 3 && text.includes(curCo)) w.push(T(`El texto contiene el nombre de su empresa actual ("${f.contact.company}"). Eso identifica a la persona.`, `The text contains their current company ("${f.contact.company}"). That identifies the person.`));
    const client = (companyName || "").trim().toLowerCase();
    if (curCo && client && (curCo.includes(client) || client.includes(curCo))) w.push(T("Esta persona trabaja en la empresa del propio cliente.", "This person works at the client's own company."));
    const vetoed = (companies || []).filter(c => c.status === "vetoed").map(c => c.name.toLowerCase());
    if (curCo && vetoed.some(v => curCo.includes(v) || v.includes(curCo))) w.push(T("Esta persona trabaja en una empresa que el cliente ha vetado.", "This person works at a company the client has vetoed."));
    return w;
  };

  const closeForm = () => { setPf(null); setPfEditing(null); setPfConfirm(false); setContactQ(""); setContactHits([]); };

  const saveProfile = async (publish) => {
    const f = pf;
    if (!f.role_title.trim()) return;
    const warns = warningsFor(f);
    if (publish && warns.length && !pfConfirm) { setPfConfirm(true); return; }
    setBusy(true);
    const row = {
      position_id: position.id, role_title: f.role_title.trim(), company_descriptor: f.company_descriptor.trim() || null,
      years_experience: f.years_experience === "" ? null : Number(f.years_experience) || null, location: f.location.trim() || null,
      background: f.background.trim() || null, highlights: f.highlights.split("\n").map(s => s.trim()).filter(Boolean).slice(0, 3),
      published: !!publish,
    };
    const q = pfEditing
      ? supabase.from("mapping_profiles").update(row).eq("id", pfEditing).select()
      : supabase.from("mapping_profiles").insert({ ...row, sort_order: (profiles || []).length + 1 }).select();
    const { data, error } = await q;
    if (error || !data?.length) { setBusy(false); flash(false, T("No se pudo guardar el perfil: ", "Couldn't save the profile: ") + (error?.message || "")); return; }
    const saved = data[0];
    // Enlace con la persona real (solo admin)
    if (f.contact || f.company_id) {
      const link = { profile_id: saved.id, person_id: f.contact?.person_id || null, contact_id: f.contact?.id || null, company_id: f.company_id || null };
      const { data: l } = await supabase.from("mapping_profile_links").upsert(link).select();
      if (l?.length) setLinks(m => ({ ...m, [saved.id]: l[0] }));
    }
    setProfiles(list => (pfEditing ? list.map(x => (x.id === saved.id ? saved : x)) : [...list, saved]));
    closeForm(); setBusy(false);
  };

  const editProfile = async (p) => {
    const l = links[p.id];
    let contact = null;
    if (l?.contact_id) {
      const { data } = await supabase.from("contacts").select("id, person_id, name, position, company, sector, city, linkedin, email").eq("id", l.contact_id).maybeSingle();
      contact = data || null;
    }
    setPf({ role_title: p.role_title || "", company_descriptor: p.company_descriptor || "", years_experience: p.years_experience ?? "", location: p.location || "", background: p.background || "", highlights: (p.highlights || []).join("\n"), contact, company_id: l?.company_id || "" });
    setPfEditing(p.id); setPfConfirm(false); setContactQ(contact?.name || "");
  };

  // Publicar pasa por el formulario y sus avisos; despublicar es directo
  const togglePublish = async (p) => {
    if (!p.published) { await editProfile(p); return; }
    const { data, error } = await supabase.from("mapping_profiles").update({ published: false }).eq("id", p.id).select();
    if (error || !data?.length) { flash(false, T("No se pudo despublicar", "Couldn't unpublish")); return; }
    setProfiles(list => list.map(x => (x.id === p.id ? data[0] : x)));
  };

  // "Pasar a candidato": aquí ya hay consentimiento. Crea la candidatura con
  // el flujo de siempre y deja constancia en el enlace.
  const promote = async (p) => {
    const l = links[p.id];
    setAsk(null);
    if (!l?.contact_id) return;
    setBusy(true);
    // Mismo camino que "Añadir desde la base" (lib/personCandidate.js)
    const person = await loadPersonData(l.person_id || null, l.contact_id);
    if (!person.name) { setBusy(false); flash(false, T("No se encontró el contacto", "Contact not found")); return; }
    const dup = await findExistingCandidacy(position.id, person);
    if (dup) { setBusy(false); flash(false, T(`${dup.name || person.name} ya es candidato de esta posición.`, `${dup.name || person.name} is already a candidate for this position.`)); return; }
    const { nc, error, photoUrl } = await createCandidateFromPerson(person, position);
    if (photoUrl) setPhotoUrls(pu => ({ ...pu, [nc.id]: photoUrl }));
    if (error) { setBusy(false); flash(false, T("No se pudo crear la candidatura: ", "Couldn't create the candidate: ") + error.message); return; }
    const { data: l2 } = await supabase.from("mapping_profile_links").update({ promoted_candidate_id: nc.id }).eq("profile_id", p.id).select();
    if (l2?.length) setLinks(m => ({ ...m, [p.id]: l2[0] }));
    setOpenPositionsList(prev => prev.map(x => (x.id === position.id ? { ...x, candidates: [nc, ...(x.candidates || [])] } : x)));
    setBusy(false);
    flash(true, T(`${person.name} ya está en Candidatos.`, `${person.name} is now in Candidates.`));
  };

  // ── Despliegue de filas y acciones ──
  const isOpen = (kind, id) => !!open && open.kind === kind && open.id === id;
  const resetRow = () => { setVetoing(false); setVetoText(""); setCommentText(""); setAsk(null); };
  const toggleRow = (kind, id) => { resetRow(); setOpen(isOpen(kind, id) ? null : { kind, id }); };
  const openRow = (kind, id) => { if (!isOpen(kind, id)) { resetRow(); setOpen({ kind, id }); } };
  const clickValidate = (kind, row) => setStatus(kind, row, row.status === "validated" ? "proposed" : "validated");
  const clickVeto = (kind, row) => {
    if (row.status === "vetoed") { setStatus(kind, row, "proposed"); return; }
    openRow(kind, row.id); setAsk(null); setVetoing(true); setVetoText("");
  };
  const askDelete = (kind, row) => { openRow(kind, row.id); setVetoing(false); setAsk({ type: "delete", kind, id: row.id }); };
  const askPromote = (p) => {
    if (!links[p.id]?.contact_id) { flash(false, T("Este perfil no está enlazado a un contacto: edítalo y elige el contacto.", "This profile isn't linked to a contact: edit it and pick the contact.")); return; }
    openRow("profile", p.id); setVetoing(false); setAsk({ type: "promote", kind: "profile", id: p.id });
  };
  const openNewProfile = () => { setPf({ ...EMPTY_PROFILE }); setPfEditing(null); setPfConfirm(false); setContactQ(""); };

  // ── Estilos (valores del sistema de diseño) ──
  const card = { background: WHITE, border: `1.5px solid ${LINE}`, borderRadius: 14 };
  // Margen lateral común de cabeceras, filas y pies (como las tarjetas de Documentos)
  const pad = isMobile ? 16 : 20;
  const label = { fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4 };
  const control = { width: "100%", height: isMobile ? 44 : 38, boxSizing: "border-box", border: `1.5px solid ${LINE}`, borderRadius: 8, fontSize: isMobile ? 16 : 13, fontWeight: 500, fontFamily: "inherit", color: INK, background: WHITE, outline: "none", padding: "0 12px" };
  const area = { ...control, height: "auto", minHeight: isMobile ? 88 : undefined, padding: "10px 12px", resize: "vertical", lineHeight: 1.55 };
  const focus = { onFocus: e => e.target.style.borderColor = BRAND, onBlur: e => e.target.style.borderColor = LINE };
  // Botones y enlaces del sistema (constants.js), con el alto de esta pantalla
  const btnBox = { padding: "0 16px", height: isMobile ? 44 : 38, whiteSpace: "nowrap", boxSizing: "border-box" };
  const ghost = { ...btn.ghost, ...btnBox };
  const solid = (dis, bg = BRAND) => ({ ...(dis ? btn.disabled : bg === DANGER ? btn.danger : btn.primary), ...btnBox });
  const textLink = (color) => ({ ...linkBtn, ...(color === BRAND ? null : { color: MUTED, fontWeight: 600 }), display: "inline-flex", alignItems: "center", gap: 5, minHeight: isMobile ? 44 : 32, ...(isMobile ? { fontSize: 13 } : null) });
  const stateChip = { ...chip(false), display: "inline-block", flexShrink: 0 };
  const ellipsis = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
  const btnSize = isMobile ? 40 : 32;
  // Botón redondo ✓ / ✕: gris por defecto, relleno cuando es el estado elegido
  const roundBtn = (on, onBg) => ({ width: btnSize, height: btnSize, borderRadius: 999, flexShrink: 0, padding: 0, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", border: `1.5px solid ${on ? onBg : LINE}`, background: on ? onBg : WHITE, color: on ? WHITE : MUTED });
  const hover = (on, color) => (on ? {} : { onMouseEnter: e => { e.currentTarget.style.color = color; }, onMouseLeave: e => { e.currentTarget.style.color = MUTED; } });
  const threadOf = (kind, id) => comments.filter(c => c.target_type === kind && c.target_id === id);
  const fmtDate = (d) => {
    const dt = new Date(d);
    return dt.toLocaleDateString(es ? "es-ES" : "en-GB", { day: "numeric", month: "short", ...(dt.getFullYear() !== new Date().getFullYear() ? { year: "numeric" } : {}) });
  };
  const stop = (fn) => (e) => { e.stopPropagation(); fn(); };

  if (companies === null || profiles === null) return <div style={{ ...card, padding: "28px 24px", ...text.body, color: MUTED, maxWidth: 760 }}>{T("Cargando el mapping...", "Loading the mapping...")}</div>;

  // El cliente solo recibe los publicados (RLS); al admin le llegan también
  // los borradores, que no cuentan para lo que el cliente tiene que revisar
  const published = profiles.filter(p => p.published);
  const pendCo = companies.filter(c => c.status === "proposed").length;
  const pendPf = published.filter(p => p.status === "proposed").length;

  // ── Cabecera: qué toca ahora y una sola acción ──
  const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
  const todo = [
    pendCo ? plural(pendCo, T("empresa", "company"), T("empresas", "companies")) : null,
    pendPf ? plural(pendPf, T("perfil", "profile"), T("perfiles", "profiles")) : null,
  ].filter(Boolean).join(T(" y ", " and "));
  const headline = approval.at
    ? T(`Mapping aprobado el ${fmtDate(approval.at)}`, `Mapping approved on ${fmtDate(approval.at)}`)
    : isAdmin
      ? T("Esperando al cliente", "Waiting for the client")
      : todo ? T(`Revisa ${todo}`, `Review ${todo}`) : T("Todo revisado", "All reviewed");
  const subline = approval.at
    ? T("Es la señal para empezar a contactar.", "It's the go-ahead to start contacting.")
    : isAdmin
      ? (todo ? T(`Le quedan por revisar ${todo}.`, `Still to review: ${todo}.`) : T("Lo ha revisado todo; falta que lo apruebe.", "Everything reviewed; approval pending."))
      : T("Perfiles anónimos: sin nombre ni empresa actual.", "Anonymised profiles: no name or current company.");
  const headAction = approval.at
    ? <button onClick={() => approve(false)} style={ghost}>{T("Retirar aprobación", "Withdraw approval")}</button>
    : !isAdmin && <button onClick={() => approve(true)} style={solid(false)}>{T("Aprobar mapping", "Approve mapping")}</button>;

  // ── Fila ──
  const rowMeta = (kind, row) => (kind === "company"
    ? [row.sector, row.added_by_role === "client" ? T(`propuesta por ${companyName || "el cliente"}`, `suggested by ${companyName || "the client"}`) : null]
    : [row.company_descriptor, row.years_experience != null ? `${row.years_experience} ${T("años", "yrs")}` : null, row.location]
  ).filter(Boolean).join(" · ");

  // Menú ⋯ del admin: la pieza común MoreMenu de shared.jsx (24/10)
  const adminMenu = (kind, row) => {
    const l = kind === "profile" ? links[row.id] : null;
    const items = kind === "profile" ? [
      { label: T("Editar", "Edit"), onClick: () => editProfile(row) },
      { label: row.published ? T("Despublicar", "Unpublish") : T("Publicar…", "Publish…"), onClick: () => togglePublish(row) },
      !l?.promoted_candidate_id && { label: T("Pasar a candidato", "Promote to candidate"), onClick: () => askPromote(row) },
      { label: T("Borrar", "Delete"), onClick: () => askDelete(kind, row), danger: true },
    ] : [{ label: T("Borrar", "Delete"), onClick: () => askDelete(kind, row), danger: true }];
    return <MoreMenu items={items} label={T("Más", "More")} size={btnSize} width={200} />;
  };

  // Confirmación neutra en la página: color solo en el botón que ejecuta
  const askPanel = (kind, row) => {
    if (!ask || ask.kind !== kind || ask.id !== row.id) return null;
    const isDel = ask.type === "delete";
    const name = kind === "company" ? row.name : row.role_title;
    const text = isDel
      ? T(<>¿Borrar <strong>{name}</strong> del mapping? Se borran también sus comentarios y no se puede deshacer.</>,
          <>Delete <strong>{name}</strong> from the mapping? Its comments go with it, and it can't be undone.</>)
      : T(<>¿Pasar <strong>{name}</strong> a Candidatos? Hazlo solo si la persona ha mostrado interés y ha dado su consentimiento: a partir de ahí se ve su nombre.</>,
          <>Move <strong>{name}</strong> to Candidates? Only if the person has shown interest and consented: from then on their name is visible.</>);
    return (
      <div onClick={e => e.stopPropagation()}>
        <ConfirmBar danger={isDel} style={{ marginTop: 12 }} message={text}
          actionLabel={isDel ? T("Borrar", "Delete") : T("Pasar a candidato", "Promote to candidate")}
          cancelLabel={T("Cancelar", "Cancel")} busy={!isDel && busy}
          onConfirm={() => (isDel ? removeRow(kind, row) : promote(row))} onCancel={() => setAsk(null)} />
      </div>
    );
  };

  // Lo que se ve al desplegar la fila
  const detail = (kind, row) => {
    const isCo = kind === "company";
    const thread = threadOf(kind, row.id);
    const body = { fontSize: 13, color: TEXT, lineHeight: 1.55 };
    return (
      <div style={{ padding: `0 ${pad}px 16px` }} onClick={e => e.stopPropagation()}>
        {isCo && row.reason && <div style={body}>{row.reason}</div>}
        {!isCo && row.background && <div style={body}>{row.background}</div>}
        {!isCo && (row.highlights || []).map((h, i) => <div key={i} style={{ ...body, display: "flex", gap: 6, marginTop: 2 }}><span style={{ color: FAINT }}>•</span>{h}</div>)}
        {row.status === "vetoed" && row.veto_reason && <div style={{ fontSize: 13, color: MUTED, marginTop: 6 }}>{T("Motivo del veto: ", "Veto reason: ")}{row.veto_reason}</div>}

        {vetoing && (
          <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: isMobile ? "wrap" : "nowrap" }}>
            <input autoFocus value={vetoText} onChange={e => setVetoText(e.target.value)} onKeyDown={e => { if (e.key === "Enter") setStatus(kind, row, "vetoed", vetoText.trim() || null); }}
              placeholder={T("¿Por qué la vetas? (opcional)", "Why veto it? (optional)")} style={{ ...control, flex: isMobile ? "1 1 100%" : 1, minWidth: 0 }} {...focus} />
            <button onClick={() => { setVetoing(false); setVetoText(""); }} style={{ ...ghost, flex: isMobile ? 1 : "none" }}>{T("Cancelar", "Cancel")}</button>
            <button onClick={() => setStatus(kind, row, "vetoed", vetoText.trim() || null)} style={{ ...solid(false), flex: isMobile ? 1 : "none" }}>{T("Vetar", "Veto")}</button>
          </div>
        )}
        {askPanel(kind, row)}

        {/* Comentarios */}
        <div style={{ marginTop: 12 }}>
          {thread.map(c => (
            <div key={c.id} style={{ marginBottom: 8, fontSize: 13, lineHeight: 1.5 }}>
              <strong style={{ color: INK }}>{c.author_label}</strong>
              <span style={{ fontSize: 11, color: MUTED, marginLeft: 8 }}>{fmtDate(c.created_at)}</span>
              <div style={{ color: TEXT, whiteSpace: "pre-wrap" }}>{c.body}</div>
            </div>
          ))}
          <div style={{ display: "flex", gap: 8 }}>
            <input value={commentText} onChange={e => setCommentText(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") addComment(kind, row.id); }}
              placeholder={T("Escribe un comentario…", "Write a comment…")} style={{ ...control, flex: 1, minWidth: 0 }} {...focus} />
            <button onClick={() => addComment(kind, row.id)} disabled={!commentText.trim()} style={solid(!commentText.trim())}>{T("Enviar", "Send")}</button>
          </div>
        </div>
      </div>
    );
  };

  const rowView = (kind, row, first) => {
    const isCo = kind === "company";
    const on = isOpen(kind, row.id);
    const n = threadOf(kind, row.id).length;
    const meta = rowMeta(kind, row);
    const l = isCo ? null : links[row.id];
    const validated = row.status === "validated";
    const vetoed = row.status === "vetoed";
    return (
      <div key={`${kind}-${row.id}`} style={{ borderTop: `1px solid ${ROW_LINE}`, background: on ? SURFACE : "transparent" }}>
        <div onClick={() => toggleRow(kind, row.id)}
          style={{ display: "flex", alignItems: "center", gap: isMobile ? 6 : 8, padding: `9px ${isMobile ? 10 : 12}px 9px ${pad}px`, minHeight: 44, boxSizing: "border-box", cursor: "pointer" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Fila de lista: 13/600 y debajo 12 gris, como el entrevistador de Evaluación */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: INK, ...ellipsis }} title={isCo ? row.name : row.role_title}>{isCo ? row.name : row.role_title}</span>
              {!isCo && isAdmin && !row.published && <span style={stateChip}>{T("Borrador", "Draft")}</span>}
              {isAdmin && l?.promoted_candidate_id && <span style={stateChip}>{T("Ya es candidato", "Now a candidate")}</span>}
            </div>
            {meta && <div style={{ fontSize: 12, color: MUTED, marginTop: 1, ...ellipsis }} title={meta}>{meta}</div>}
          </div>
          {n > 0 && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 12, fontWeight: 600, color: MUTED, fontVariantNumeric: "tabular-nums", flexShrink: 0, marginRight: 2 }}>
              <MessageSquare size={14} strokeWidth={1.75} />{n}
            </span>
          )}
          <button onClick={stop(() => clickValidate(kind, row))} title={validated ? T("Validado · pulsa para deshacer", "Validated · click to undo") : T("Validar", "Validate")}
            style={roundBtn(validated, BRAND)} {...hover(validated, BRAND)}>
            <Check size={16} strokeWidth={2.25} />
          </button>
          <button onClick={stop(() => clickVeto(kind, row))} title={vetoed ? T("Vetado · pulsa para deshacer", "Vetoed · click to undo") : T("Vetar", "Veto")}
            style={roundBtn(vetoed, MUTED)} {...hover(vetoed, DANGER)}>
            <X size={16} strokeWidth={2.25} />
          </button>
          {isAdmin && adminMenu(kind, row)}
        </div>
        {on && detail(kind, row)}
      </div>
    );
  };

  // ── Sección: lista + enlaces al pie ──
  const section = (kind, title, list, empty, addLink) => {
    const vetoedRows = list.filter(x => x.status === "vetoed");
    const rows = [...list.filter(x => x.status === "proposed"), ...list.filter(x => x.status === "validated"), ...(showVetoed[kind] ? vetoedRows : [])];
    const coWord = kind === "company";
    // Móvil: contraída al entrar, la cabecera dice cuántas quedan por revisar.
    // Escritorio: siempre desplegada, sin flecha.
    const isOpen = !isMobile || !!openSec[kind];
    const pending = list.filter(x => x.status === "proposed").length;
    // Una tarjeta por sección, con el título dentro (como Documentos y
    // Evaluación): cabecera, filas y un pie con las acciones.
    const headIn = (
      <>
        {isMobile && <ChevronDown size={18} strokeWidth={1.75} color={MUTED} style={{ transform: isOpen ? "none" : "rotate(-90deg)", transition: "transform 0.15s", flexShrink: 0 }} />}
        <span style={text.cardTitle}>{title} <span style={{ fontWeight: 400, color: MUTED }}>({list.length})</span></span>
        {pending > 0 && <span style={{ fontSize: 12, color: MUTED, marginLeft: "auto" }}>{T(`${pending} por revisar`, `${pending} to review`)}</span>}
      </>
    );
    const headStyle = { display: "flex", alignItems: "center", gap: 8, width: "100%", boxSizing: "border-box", padding: `14px ${pad}px`, minHeight: isMobile ? 48 : undefined };
    const hasFoot = !!addLink || vetoedRows.length > 0;
    return (
      <div style={card}>
        {isMobile ? (
          <button onClick={() => setOpenSec(o => ({ ...o, [kind]: !o[kind] }))} aria-expanded={isOpen}
            style={{ ...headStyle, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
            {headIn}
          </button>
        ) : <div style={headStyle}>{headIn}</div>}
        {isOpen && <>
        {list.length === 0
          ? <div style={{ borderTop: `1px solid ${ROW_LINE}`, padding: `14px ${pad}px`, fontSize: 13, color: MUTED, lineHeight: 1.6 }}>{empty}</div>
          : rows.map((r, i) => rowView(kind, r, i === 0))}
        {list.length > 0 && rows.length === 0 && (
          <div style={{ borderTop: `1px solid ${ROW_LINE}`, padding: `14px ${pad}px`, fontSize: 13, color: MUTED }}>{coWord ? T("Todas están vetadas.", "All of them are vetoed.") : T("Todos están vetados.", "All of them are vetoed.")}</div>
        )}
        {kind === "company" && addingCo && (
          <div style={{ display: "flex", gap: 8, padding: `12px ${pad}px`, borderTop: `1px solid ${ROW_LINE}`, flexWrap: isMobile ? "wrap" : "nowrap" }}>
            <input autoFocus value={newCo.name} onChange={e => setNewCo(f => ({ ...f, name: e.target.value }))} onKeyDown={e => { if (e.key === "Enter") addCompany(); }}
              placeholder={T("Empresa *", "Company *")} style={{ ...control, flex: isMobile ? "1 1 100%" : 2, minWidth: 0 }} {...focus} />
            <input value={newCo.sector} onChange={e => setNewCo(f => ({ ...f, sector: e.target.value }))} onKeyDown={e => { if (e.key === "Enter") addCompany(); }}
              placeholder={T("Sector", "Sector")} style={{ ...control, flex: isMobile ? "1 1 100%" : 1, minWidth: 0 }} {...focus} />
            <button onClick={() => { setAddingCo(false); setNewCo({ name: "", sector: "" }); }} style={{ ...ghost, flex: isMobile ? 1 : "none" }}>{T("Cancelar", "Cancel")}</button>
            <button onClick={addCompany} disabled={!newCo.name.trim()} style={{ ...solid(!newCo.name.trim()), flex: isMobile ? 1 : "none" }}>{isAdmin ? T("Añadir", "Add") : T("Proponer", "Suggest")}</button>
          </div>
        )}
        {hasFoot && <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: `8px ${pad}px`, borderTop: `1px solid ${ROW_LINE}`, minHeight: 40, boxSizing: "border-box" }}>
          <span>{addLink}</span>
          {vetoedRows.length > 0 && (
            <button onClick={() => setShowVetoed(s => ({ ...s, [kind]: !s[kind] }))} style={textLink(MUTED)}>
              {showVetoed[kind]
                ? (coWord ? T("Ocultar vetadas", "Hide vetoed") : T("Ocultar vetados", "Hide vetoed"))
                : (coWord ? T(`Ver ${vetoedRows.length} vetada${vetoedRows.length === 1 ? "" : "s"}`, `Show ${vetoedRows.length} vetoed`) : T(`Ver ${vetoedRows.length} vetado${vetoedRows.length === 1 ? "" : "s"}`, `Show ${vetoedRows.length} vetoed`))}
            </button>
          )}
        </div>}
        </>}
      </div>
    );
  };

  const addCoLink = !addingCo && (
    <button onClick={() => setAddingCo(true)} style={textLink(BRAND)}>
      <Plus size={14} strokeWidth={2} />{isAdmin ? T("Añadir empresa", "Add company") : T("Proponer una empresa", "Suggest a company")}
    </button>
  );
  const addPfLink = isAdmin && (
    <button onClick={openNewProfile} style={textLink(BRAND)}>
      <Plus size={14} strokeWidth={2} />{T("Añadir perfil", "Add profile")}
    </button>
  );

  // ── Formulario de perfil (admin): hoja a pantalla completa en móvil,
  //    ventana centrada en escritorio ──
  const warns = warningsFor(pf);
  const pfDisabled = !pf || busy || !pf.role_title.trim();
  const pfSheet = isAdmin && pf && (
    <div onClick={closeForm} style={{ position: "fixed", inset: 0, zIndex: 120, background: isMobile ? WHITE : SCRIM, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()} style={isMobile
        ? { position: "absolute", inset: 0, background: WHITE, display: "flex", flexDirection: "column", paddingTop: "env(safe-area-inset-top)", boxSizing: "border-box" }
        : { ...card, width: "min(560px, calc(100vw - 32px))", maxHeight: "88vh", display: "flex", flexDirection: "column", boxShadow: SHADOW_MODAL, boxSizing: "border-box" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "6px 8px 6px 16px", borderBottom: `1px solid ${LINE}`, flexShrink: 0 }}>
          <span style={text.cardTitle}>{pfEditing ? T("Editar perfil", "Edit profile") : T("Nuevo perfil", "New profile")}</span>
          <CloseButton onClick={closeForm} label={T("Cerrar", "Close")} />
        </div>
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: 16 }}>
          <label style={{ ...label, display: "block", marginBottom: 6 }}>{T("Contacto de tu base (solo lo ves tú)", "Contact from your database (only you see this)")}</label>
          <div style={{ position: "relative", marginBottom: 12 }}>
            <input value={contactQ} onChange={e => searchContacts(e.target.value)} placeholder={T("Busca por nombre: la IA propone el descriptor", "Search by name: the AI drafts the descriptor")} style={control} {...focus} />
            {contactHits.length > 0 && (
              <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: WHITE, border: `1.5px solid ${LINE}`, borderRadius: 8, marginTop: 4, zIndex: 20, boxShadow: SHADOW_POP }}>
                {contactHits.map(ct => (
                  <button key={ct.id} onClick={() => pickContact(ct)} style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", borderBottom: `1px solid ${ROW_LINE}`, padding: isMobile ? "11px 12px" : "8px 12px", cursor: "pointer", fontFamily: "inherit", fontSize: 13 }}>
                    <strong>{ct.name}</strong> <span style={{ color: MUTED }}>{[ct.position, ct.company, ct.city].filter(Boolean).join(" · ")}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {drafting && <div style={{ fontSize: 12, color: MUTED, marginBottom: 10 }}>{T("Preparando el descriptor anónimo...", "Drafting the anonymised descriptor...")}</div>}
          <div style={{ paddingTop: 14, borderTop: `1px solid ${ROW_LINE}` }}>
            <label style={{ ...label, display: "block", marginBottom: 6 }}>{T("Lo que ve el cliente", "What the client sees")}</label>
            <input value={pf.role_title} onChange={e => setPf(f => ({ ...f, role_title: e.target.value }))} placeholder={T("Cargo genérico *", "Generic role *")} style={{ ...control, marginBottom: 8 }} {...focus} />
            <input value={pf.company_descriptor} onChange={e => setPf(f => ({ ...f, company_descriptor: e.target.value }))} placeholder={T("Empresa genérica (p. ej. scale-up SaaS Serie B)", "Generic company (e.g. SaaS scale-up, Series B)")} style={{ ...control, marginBottom: 8 }} {...focus} />
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <input value={pf.years_experience} onChange={e => setPf(f => ({ ...f, years_experience: e.target.value.replace(/[^0-9]/g, "") }))} placeholder={T("Años", "Years")} inputMode="numeric" style={{ ...control, width: 90, flexShrink: 0 }} {...focus} />
              <input value={pf.location} onChange={e => setPf(f => ({ ...f, location: e.target.value }))} placeholder={T("Ubicación", "Location")} style={{ ...control, flex: 1, minWidth: 0 }} {...focus} />
            </div>
            <input value={pf.background} onChange={e => setPf(f => ({ ...f, background: e.target.value }))} placeholder={T("Formación y trayectoria, sin nombres de empresas", "Education and career, no company names")} style={{ ...control, marginBottom: 8 }} {...focus} />
            <textarea value={pf.highlights} onChange={e => setPf(f => ({ ...f, highlights: e.target.value }))} rows={3} placeholder={T("2-3 puntos destacados, uno por línea", "2-3 highlights, one per line")} style={{ ...area, marginBottom: 12 }} {...focus} />
          </div>
          <label style={{ ...label, display: "block", marginBottom: 6 }}>{T("Empresa objetivo (solo para ti)", "Target company (only for you)")}</label>
          <select value={pf.company_id} onChange={e => setPf(f => ({ ...f, company_id: e.target.value }))} style={{ ...control, marginBottom: 12, cursor: "pointer" }}>
            <option value="">{T("Ninguna", "None")}</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {warns.length > 0 && (
            <div style={{ background: WHITE, border: `1.5px solid ${LINE}`, borderRadius: 10, padding: "10px 14px" }}>
              {warns.map((w, i) => (
                <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12, color: DANGER, lineHeight: 1.5 }}>
                  <TriangleAlert size={14} strokeWidth={1.75} style={{ flexShrink: 0, marginTop: 2 }} />{w}
                </div>
              ))}
              {pfConfirm && <div style={{ fontSize: 12, color: INK, marginTop: 6 }}>{T("Pulsa Publicar otra vez para confirmar que lo has revisado.", "Press Publish again to confirm you've reviewed it.")}</div>}
            </div>
          )}
        </div>
        <div style={{ flexShrink: 0, borderTop: `1px solid ${LINE}`, padding: isMobile ? "12px 16px calc(12px + env(safe-area-inset-bottom))" : "12px 16px", display: "flex", gap: 8 }}>
          <button onClick={() => saveProfile(false)} disabled={pfDisabled} style={{ ...ghost, flex: 1, cursor: pfDisabled ? "not-allowed" : "pointer" }}>{T("Guardar borrador", "Save draft")}</button>
          <button onClick={() => saveProfile(true)} disabled={pfDisabled} style={{ ...solid(pfDisabled), flex: 1 }}>{pfConfirm ? T("Publicar igualmente", "Publish anyway") : T("Publicar", "Publish")}</button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
      {/* Rango salarial: el admin ve la referencia y publica; el cliente ve solo lo publicado (15/10) */}
      <SalaryReco position={position} lang={lang} isAdmin={isAdmin} setOpenPositionsList={setOpenPositionsList} />

      {/* Estado del mapping: qué toca y una acción, en su tarjeta */}
      <div style={{ ...card, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", padding: `14px ${pad}px` }}>
        <div style={{ minWidth: 0, flex: "1 1 240px" }}>
          <div style={text.cardTitle}>{headline}</div>
          <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>{subline}</div>
        </div>
        {headAction}
      </div>

      {loadErr && (
        <div style={errorBanner}>
          {T("No se pudo cargar el mapping: ", "Couldn't load the mapping: ")}{loadErr}
        </div>
      )}

      {/* Empresas y perfiles: en el ordenador, lado a lado (usa el ancho como
          las demás pestañas); en el móvil, una debajo de otra */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "repeat(auto-fit, minmax(380px, 1fr))", gap: 16, alignItems: "start" }}>
        {section("company", T("Empresas", "Companies"), companies,
          isAdmin ? T("Todavía no hay empresas.", "No companies yet.") : T("Todavía no hay empresas. Rutment las añadirá en breve; mientras, puedes proponer las que quieras.", "No companies yet. Rutment will add them shortly; meanwhile you can suggest any you like."),
          addCoLink)}

        {section("profile", T("Perfiles", "Profiles"), isAdmin ? profiles : published,
          isAdmin ? T("Todavía no hay perfiles.", "No profiles yet.") : T("Todavía no hay perfiles publicados. Te avisaremos cuando estén.", "No published profiles yet. We'll let you know when they're ready."),
          addPfLink)}
      </div>


      {pfSheet}

      {msg && (
        <div style={{ position: "fixed", left: isMobile ? 16 : "auto", right: isMobile ? 16 : 24, bottom: isMobile ? `calc(${MOBILE_BOTTOM + 12}px + env(safe-area-inset-bottom))` : 24, maxWidth: isMobile ? undefined : 420, zIndex: 130, background: WHITE, border: `1.5px solid ${LINE}`, borderRadius: 12, padding: "12px 14px", boxShadow: SHADOW_POP, fontSize: 13, lineHeight: 1.45, color: msg.ok ? GREEN_TEXT : DANGER }}>{msg.text}</div>
      )}
    </div>
  );
};

export default MappingTab;
